// Consumer-app analytics client — first-party event tracking + consent
// storage. docs/replan-v2.md R8, stubbed per CLAUDE.md §37.1.
//
// track() is fire-and-forget: analytics must never break or slow a user flow.
// The session cookie rides along (credentials: include in lib/api.ts), so the
// API attaches userId server-side; anonymousId stitches pre-auth activity.
// NO ad pixels load in this app — kid surfaces live here (CLAUDE.md §29).

import {
  ANONYMOUS_ID_KEY,
  CONSENT_COOKIE,
  CONSENT_COOKIE_MAX_AGE_DAYS,
  CONSENT_POLICY_VERSION,
  consentFromCookieString,
  serializeConsent,
  type ConsentChoices,
  type EventName,
  type StoredConsent,
} from '@fajrarabi/analytics';

import { api } from './api';

export function anonymousId(): string {
  try {
    let id = localStorage.getItem(ANONYMOUS_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(ANONYMOUS_ID_KEY, id);
    }
    return id;
  } catch {
    // Storage blocked (private mode) — a per-page id is the best we can do.
    return crypto.randomUUID();
  }
}

/** Record a first-party product event. Never throws, never awaited by callers. */
export function track(name: EventName, properties?: Record<string, unknown>): void {
  void api('/events/client', {
    method: 'POST',
    body: {
      source: 'app',
      anonymousId: anonymousId(),
      events: [{ name, properties: properties ?? {}, occurredAt: new Date().toISOString() }],
    },
  }).catch(() => undefined);
}

export function readConsent(): StoredConsent | null {
  if (typeof document === 'undefined') return null;
  return consentFromCookieString(document.cookie);
}

/** Persist a consent decision: cookie (1 year) + append-only API record. */
export function saveConsent(choices: ConsentChoices): StoredConsent {
  const stored: StoredConsent = { v: CONSENT_POLICY_VERSION, c: choices, at: Date.now() };
  const maxAge = CONSENT_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${CONSENT_COOKIE}=${serializeConsent(stored)}; Max-Age=${maxAge}; Path=/; SameSite=Lax; Secure`;
  void api('/events/consent', {
    method: 'POST',
    body: {
      policyVersion: CONSENT_POLICY_VERSION,
      anonymousId: anonymousId(),
      choices: { analytics: choices.analytics, marketing: choices.marketing },
    },
  }).catch(() => undefined);
  return stored;
}
